import React, { useEffect, useMemo, useState } from "react";

export default function ReceiptModal({ open, receipt, onClose }) {
  const [copied, setCopied] = useState(false);
  const view = useMemo(() => normalizeReceipt(receipt), [receipt]);

  useEffect(() => {
    if (!open) return;
    function onKeyDown(e) {
      if (e.key === "Escape") onClose?.();
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open || !view) return null;

  async function copyRef() {
    try {
      await navigator.clipboard.writeText(view.reference);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch (_) {}
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40"
      role="dialog"
      aria-modal="true"
      aria-label="Détails de l’opération"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className="fixed inset-0 flex items-end sm:items-center justify-center p-0 sm:p-4">
        <div
          className="
            w-full sm:max-w-lg
            bg-white dark:bg-slate-900
            sm:rounded-2xl rounded-t-3xl
            shadow-xl border border-black/10 dark:border-white/10
            overflow-hidden
            max-h-[92vh] sm:max-h-[85vh]
            flex flex-col
          "
        >
          <div className="sm:hidden flex justify-center pt-2">
            <div className="h-1.5 w-10 rounded-full bg-black/10 dark:bg-white/10" />
          </div>

          <div className="p-5 border-b border-black/10 dark:border-white/10">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <h3 className="text-base font-semibold text-slate-900 dark:text-white">{view.headerTitle}</h3>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  {view.typeLabel} • {view.statusLabel}
                </p>

                {view.status === "pending" && (
                  <div className="mt-3 rounded-2xl border border-amber-500/20 bg-amber-500/10 px-3 py-2 text-xs text-amber-800 dark:text-amber-200">
                    Cette opération est <b>en attente</b> de validation administrateur.
                  </div>
                )}
              </div>

              <button
                onClick={onClose}
                className="shrink-0 rounded-2xl px-3 py-2 text-sm border border-black/10 dark:border-white/10 hover:bg-black/5 dark:hover:bg-white/10"
              >
                Fermer
              </button>
            </div>
          </div>

          <div className="p-5 space-y-4 overflow-y-auto">
            <InfoRow
              label="Référence"
              value={view.reference}
              right={
                <button onClick={copyRef} className="text-sm font-medium text-blue-600 dark:text-blue-400">
                  {copied ? "Copié ✅" : "Copier"}
                </button>
              }
            />

            <Divider />

            {/* ✅ Amount display: local first (if available) then ledger */}
            <InfoRow
              label="Montant"
              value={
                view.fx
                  ? `${fmtMoney(view.fx.displayAmount, view.fx.to)} (Local)`
                  : fmtMoney(view.amountCad, "CAD")
              }
            />
            {view.fx && (
              <InfoRow label="Montant (ledger)" value={fmtMoney(view.amountCad, "CAD")} />
            )}

            {Number(view.feesCad) > 0 && (
              <InfoRow label="Frais" value={fmtMoney(view.feesCad, "CAD")} />
            )}

            {view.method && <InfoRow label="Méthode" value={prettyMethod(view.method)} />}

            {view.counterpartyName && (
              <InfoRow
                label={view.type === "transfer_out" ? "Bénéficiaire" : "Contrepartie"}
                value={`${view.counterpartyName}${view.counterpartyPhone ? ` (${view.counterpartyPhone})` : ""}`}
              />
            )}

            <Divider />

            {/* ✅ balances in local when fx exists */}
            <InfoRow
              label="Solde (après)"
              value={
                view.fx
                  ? `${fmtMoney(cadToLocal(view.balanceAfterCad, view.fx.rate), view.fx.to)} (Local)`
                  : fmtMoney(view.balanceAfterCad, "CAD")
              }
            />
            {view.fx && <InfoRow label="Solde (après, ledger)" value={fmtMoney(view.balanceAfterCad, "CAD")} />}

            {view.balanceBeforeCad != null && (
              <>
                <InfoRow
                  label="Solde (avant)"
                  value={
                    view.fx
                      ? `${fmtMoney(cadToLocal(view.balanceBeforeCad, view.fx.rate), view.fx.to)} (Local)`
                      : fmtMoney(view.balanceBeforeCad, "CAD")
                  }
                />
                {view.fx && <InfoRow label="Solde (avant, ledger)" value={fmtMoney(view.balanceBeforeCad, "CAD")} />}
              </>
            )}

            <InfoRow label="Date" value={view.timestamp ? new Date(view.timestamp).toLocaleString() : "—"} />
            {view.note && <InfoRow label="Note" value={view.note} />}

            {/* Optional: show rate */}
            {view.fx?.rate && (
              <InfoRow
                label="Taux"
                value={`1 CAD = ${Number(view.fx.rate).toFixed(6)} ${view.fx.to}`}
              />
            )}
          </div>

          <div className="border-t border-black/10 dark:border-white/10 p-5 pb-[calc(env(safe-area-inset-bottom)+92px)] sm:pb-5 bg-white dark:bg-slate-900">
            <button
              onClick={onClose}
              className="w-full rounded-2xl bg-slate-900 text-white py-3 font-medium hover:opacity-95 transition dark:bg-white dark:text-slate-900"
            >
              Terminer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoRow({ label, value, right }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        <div className="text-xs text-slate-500 dark:text-slate-400">{label}</div>
        <div className="mt-1 text-sm font-medium text-slate-900 dark:text-white break-words">{value}</div>
      </div>
      {right}
    </div>
  );
}

function Divider() {
  return <div className="h-px bg-black/5 dark:bg-white/10" />;
}

function normalizeReceipt(receipt) {
  if (!receipt) return null;

  const reference = receipt.reference || "—";
  const type = receipt.type || "—";
  const status = receipt.status || "—";

  const typeLabel =
    type === "fund" ? "Recharge wallet" :
    type === "withdraw" ? "Retrait wallet" :
    type === "transfer_out" ? "Transfert sortant" :
    String(type);

  const statusLabel =
    status === "completed" ? "Confirmé" :
    status === "pending" ? "En attente (validation admin)" :
    status === "failed" ? "Rejeté" :
    status === "cancelled" ? "Annulé" :
    String(status);

  const headerTitle =
    status === "completed" ? "Opération confirmée" :
    status === "pending" ? "Opération enregistrée" :
    status === "failed" ? "❌ Opération rejetée" :
    "Détails de l’opération";

  const amountCad = receipt.amount ?? "0.00";
  const method = receipt.method ?? null;

  const feesCad = receipt.fees?.amount ?? "0.00";

  const balanceAfterCad = receipt.balanceAfter?.amount ?? receipt.balanceAfter ?? "—";
  const balanceBeforeCad = receipt.balanceBefore?.amount ?? receipt.balanceBefore ?? null;

  const timestamp = receipt.timestamp ?? null;
  const note = receipt.note ?? null;

  const counterpartyName = receipt.counterparty?.name ?? null;
  const counterpartyPhone = receipt.counterparty?.phone ?? null;

  const fx = receipt.fx
    ? {
        rate: receipt.fx.rate,
        from: receipt.fx.from,
        to: receipt.fx.to,
        displayAmount: receipt.fx.displayAmount,
      }
    : null;

  return {
    reference,
    type,
    status,
    typeLabel,
    statusLabel,
    headerTitle,
    amountCad,
    method,
    feesCad,
    balanceAfterCad,
    balanceBeforeCad,
    timestamp,
    note,
    counterpartyName,
    counterpartyPhone,
    fx,
  };
}

function fmtMoney(amount, currency) {
  if (amount === null || amount === undefined || amount === "") return "—";
  const n = Number(amount);
  if (!Number.isFinite(n)) return `${amount} ${currency || ""}`.trim();
  return `${n.toFixed(2)} ${currency || ""}`.trim();
}

function cadToLocal(cadAmount, rateLocalPerCad) {
  const c = Number(cadAmount);
  const r = Number(rateLocalPerCad);
  if (!Number.isFinite(c) || !Number.isFinite(r)) return cadAmount;
  return c * r;
}

function prettyMethod(m) {
  const x = String(m || "").toLowerCase();
  if (x === "mobile_money") return "Mobile Money";
  if (x === "bank_transfer") return "Virement bancaire";
  if (x === "interac") return "Interac";
  if (x === "wallet") return "Wallet";
  return m;
}
